import { createTheme } from '@mui/material/styles';

// 🍎 Apple-Inspired Color Palette
const appleColors = {
  // Primary Apple Blues
  appleBlue: '#007AFF',
  appleBlueLight: '#5AC8FA',
  appleBlueDark: '#0051D5',
  
  // Apple Grays (System Colors)
  systemGray: '#8E8E93',
  systemGray2: '#AEAEB2',
  systemGray3: '#C7C7CC',
  systemGray4: '#D1D1D6',
  systemGray5: '#E5E5EA',
  systemGray6: '#F2F2F7',
  
  // Apple Backgrounds
  backgroundPrimary: '#FFFFFF',
  backgroundSecondary: '#F2F2F7',
  backgroundTertiary: '#FFFFFF',
  
  // Apple Text Colors
  labelPrimary: '#000000',
  labelSecondary: '#3C3C43',
  labelTertiary: '#3C3C4399',
  labelQuaternary: '#3C3C432E',
  
  // Apple Accent Colors
  green: '#34C759',
  red: '#FF3B30',
  orange: '#FF9500',
  yellow: '#FFCC00',
  purple: '#AF52DE',
  pink: '#FF2D92',
  
  // Glass Effect
  glassMorphism: 'rgba(255, 255, 255, 0.7)',
  glassMorphismDark: 'rgba(0, 0, 0, 0.05)',
};

// 🍎 Apple Typography
const appleTypography = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    'San Francisco',
    'SF Pro Display',
    'SF Pro Text',
    'Segoe UI',
    'Roboto',
    'Helvetica Neue',
    'Arial',
    'sans-serif'
  ].join(','),
  
  // Apple's typography scale
  h1: {
    fontSize: '2.75rem', // 44px
    fontWeight: 700,
    lineHeight: 1.2,
    letterSpacing: '-0.02em',
  },
  h2: {
    fontSize: '2.25rem', // 36px
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h3: {
    fontSize: '1.875rem', // 30px
    fontWeight: 600,
    lineHeight: 1.3,
    letterSpacing: '-0.01em',
  },
  h4: {
    fontSize: '1.5rem', // 24px
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h5: {
    fontSize: '1.25rem', // 20px
    fontWeight: 600,
    lineHeight: 1.4,
  },
  h6: {
    fontSize: '1.125rem', // 18px
    fontWeight: 600,
    lineHeight: 1.4,
  },
  body1: {
    fontSize: '1rem', // 16px
    fontWeight: 400,
    lineHeight: 1.5,
  },
  body2: {
    fontSize: '0.875rem', // 14px
    fontWeight: 400,
    lineHeight: 1.5,
  },
  caption: {
    fontSize: '0.75rem', // 12px
    fontWeight: 400,
    lineHeight: 1.4,
    color: appleColors.labelTertiary,
  },
};

// 🍎 Apple Spacing System (8px base unit)
const appleSpacing = {
  xs: 4,   // 0.25rem
  sm: 8,   // 0.5rem  
  md: 16,  // 1rem
  lg: 24,  // 1.5rem
  xl: 32,  // 2rem
  xxl: 48, // 3rem
  xxxl: 64, // 4rem
};

// 🍎 Apple Border Radius
const appleBorderRadius = {
  sm: 6,   // Small elements
  md: 12,  // Cards, buttons
  lg: 16,  // Large cards
  xl: 20,  // Modals, sheets
  xxl: 24, // Hero sections
  round: '50%', // Circular elements
};

// 🍎 Apple Shadows (Depth)
const appleShadows = {
  // Light shadows for cards
  card: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
  
  // Medium shadows for elevated elements
  elevated: '0 4px 6px rgba(0, 0, 0, 0.07), 0 2px 4px rgba(0, 0, 0, 0.06)',
  
  // Strong shadows for modals and popovers
  modal: '0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 6px rgba(0, 0, 0, 0.06)',
  
  // Apple's signature soft glow
  glow: '0 0 20px rgba(0, 122, 255, 0.3)',
  
  // Glass morphism effect
  glass: '0 8px 32px rgba(31, 38, 135, 0.15)',
};

// 🍎 Apple Animations & Transitions
const appleTransitions = {
  // Apple's signature easing
  easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
  
  // Transition durations
  fast: '150ms',
  normal: '250ms',
  slow: '350ms',
  
  // Spring animations (for interactions)
  spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
};

// 🍎 Create Apple-Inspired MUI Theme
export const appleTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: appleColors.appleBlue,
      light: appleColors.appleBlueLight,
      dark: appleColors.appleBlueDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: appleColors.systemGray,
      light: appleColors.systemGray3,
      dark: appleColors.labelSecondary,
    },
    error: {
      main: appleColors.red,
    },
    warning: {
      main: appleColors.orange,
    },
    success: {
      main: appleColors.green,
    },
    info: {
      main: appleColors.appleBlueLight,
    },
    background: {
      default: appleColors.backgroundSecondary,
      paper: appleColors.backgroundPrimary,
    },
    text: {
      primary: appleColors.labelPrimary,
      secondary: appleColors.labelSecondary,
      disabled: appleColors.labelTertiary,
    },
    divider: appleColors.systemGray5,
    
    // Custom Apple colors
    apple: appleColors,
  },
  
  typography: appleTypography,
  
  spacing: (factor) => `${appleSpacing.sm * factor}px`,
  
  shape: {
    borderRadius: appleBorderRadius.md,
  },
  
  shadows: [
    'none',
    appleShadows.card,
    appleShadows.card,
    appleShadows.elevated,
    appleShadows.elevated,
    appleShadows.elevated,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
    appleShadows.modal,
  ],
  
  components: {
    // 🍎 Apple Button Styles
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: appleBorderRadius.md,
          textTransform: 'none',
          fontWeight: 600,
          fontSize: '1rem',
          padding: '12px 24px',
          transition: `all ${appleTransitions.normal} ${appleTransitions.easing}`,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: appleShadows.elevated,
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
            transition: `all ${appleTransitions.fast} ${appleTransitions.easing}`,
          },
        },
        contained: {
          backgroundColor: appleColors.appleBlue,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: appleColors.appleBlueDark,
          },
        },
        outlined: {
          borderColor: appleColors.systemGray4,
          color: appleColors.labelPrimary,
          '&:hover': {
            borderColor: appleColors.appleBlue,
            backgroundColor: 'rgba(0, 122, 255, 0.04)',
          },
        },
      },
    },
    
    // 🍎 Apple Card Styles
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: appleBorderRadius.lg,
          boxShadow: appleShadows.card,
          border: `1px solid ${appleColors.systemGray6}`,
          transition: `all ${appleTransitions.normal} ${appleTransitions.easing}`,
          '&:hover': {
            boxShadow: appleShadows.elevated,
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    
    // 🍎 Apple Paper Styles  
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: appleBorderRadius.lg,
          border: `1px solid ${appleColors.systemGray6}`,
        },
        elevation1: {
          boxShadow: appleShadows.card,
        },
        elevation2: {
          boxShadow: appleShadows.elevated,
        },
        elevation3: {
          boxShadow: appleShadows.modal,
        },
      },
    },
    
    // 🍎 Apple TextField Styles
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: appleBorderRadius.md,
            backgroundColor: appleColors.backgroundPrimary,
            transition: `all ${appleTransitions.normal} ${appleTransitions.easing}`,
            '& fieldset': {
              borderColor: appleColors.systemGray4,
            },
            '&:hover fieldset': {
              borderColor: appleColors.systemGray3,
            },
            '&.Mui-focused fieldset': {
              borderColor: appleColors.appleBlue,
              boxShadow: `0 0 0 3px rgba(0, 122, 255, 0.1)`,
            },
          },
        },
      },
    },
    
    // 🍎 Apple Fab Styles
    MuiFab: {
      styleOverrides: {
        root: {
          borderRadius: appleBorderRadius.round,
          boxShadow: appleShadows.elevated,
          transition: `all ${appleTransitions.normal} ${appleTransitions.easing}`,
          '&:hover': {
            boxShadow: appleShadows.modal,
            transform: 'scale(1.05)',
          },
          '&:active': {
            transform: 'scale(0.95)',
            transition: `all ${appleTransitions.fast} ${appleTransitions.easing}`,
          },
        },
      },
    },
    
    // 🍎 Apple Chip Styles
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: appleBorderRadius.md,
          fontWeight: 500,
          backgroundColor: appleColors.systemGray6,
          color: appleColors.labelPrimary,
          '&:hover': {
            backgroundColor: appleColors.systemGray5,
          },
        },
        outlined: {
          borderColor: appleColors.systemGray4,
          '&:hover': {
            borderColor: appleColors.systemGray3,
            backgroundColor: appleColors.systemGray6,
          },
        },
      },
    },
  },
});

// Export individual design tokens for use in custom components
export {
  appleColors,
  appleTypography,
  appleSpacing,
  appleBorderRadius,
  appleShadows,
  appleTransitions,
};
